import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Package, CheckCircle, AlertTriangle, MinusCircle, FileText, UserCheck, Printer, Users, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import PrintDialog from '@/components/PrintDialog';

const ProcessingInward = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const scanRef = useRef(null);

  // Multi-staff session (mirror Assortment Packing)
  const [step, setStep] = useState('staff'); // 'staff' | 'scanning'
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}]
  const [staffValue, setStaffValue] = useState('');
  const [validating, setValidating] = useState(false);

  // Bundle scanning
  const [bundleCode, setBundleCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [bundleDetails, setBundleDetails] = useState(null);
  const [receivedQty, setReceivedQty] = useState('');
  const [remarks, setRemarks] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);

  // Print dialog
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printCodes, setPrintCodes] = useState([]);

  const focusStaffInput = useCallback(() => {
    setTimeout(() => staffInputRef.current?.focus(), 80);
  }, []);

  // === MULTI-STAFF SESSION ===
  const handleAddStaff = async (e) => {
    e.preventDefault();
    const id = staffValue.trim();
    if (!id || validating) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.error('Staff already added');
      setStaffValue('');
      focusStaffInput();
      return;
    }
    setValidating(true);
    try {
      const resp = await apiClient.post('/processing-inward/validate-staff', { staff_id: id });
      setStaffList((prev) => [...prev, { staff_id: resp.data.staff_id, staff_name: resp.data.staff_name }]);
      setStaffValue('');
      toast.success(`Added ${resp.data.staff_name}`);
      focusStaffInput();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid Staff ID');
      setStaffValue('');
      focusStaffInput();
    } finally {
      setValidating(false);
    }
  };

  const handleRemoveStaff = (staff_id) => {
    setStaffList((prev) => prev.filter((s) => s.staff_id !== staff_id));
  };

  const handleStartSession = () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      return;
    }
    setStep('scanning');
    setTimeout(() => scanRef.current?.focus(), 100);
  };

  const handleLookup = async () => {
    const code = bundleCode.trim();
    if (!code) return;
    setLoading(true);
    setBundleDetails(null);
    setLastReceipt(null);
    try {
      const res = await apiClient.get(`/processing-inward/lookup?bundle_code=${encodeURIComponent(code)}`);
      setBundleDetails(res.data);
      // 2026-05-14 — RUNNING_MTR bundles always carry exactly ONE USN per
      // the single-USN rule, but the actual qty (meters) lives in
      // `bundle_qty`. Prefilling from `sent_qty` (=1) caused the operator
      // to confirm "1" for an 88M bundle and lose the meters info. Use
      // the new `bundle_qty` field; falls back to `sent_qty` for older
      // backends.
      const prefillQty = res.data.bundle_qty ?? res.data.sent_qty;
      setReceivedQty(String(prefillQty));
      setRemarks('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bundle not found');
    } finally {
      setLoading(false);
    }
  };

  const handleScanKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLookup();
    }
  };

  const sentQty = bundleDetails?.sent_qty || 0;
  // 2026-05-14 — Match comparison must run against bundle_qty (meters for
  // RUNNING_MTR, pieces otherwise), NOT against sent_qty (which is the
  // USN count = 1 for RUNNING_MTR). Without this branch, an 88M bundle
  // with usn_count=1 would always show "Excess: 87" once the operator
  // typed 88 in the prefilled box. Fall back to sent_qty for older
  // backends that don't return bundle_qty.
  const bundleQty = bundleDetails?.bundle_qty ?? sentQty;
  const qtyUnit = bundleDetails?.qty_unit || 'Pc';
  const isRunningMtr = bundleDetails?.flow_type === 'RUNNING_MTR';
  // 2026-05-18 — RUNNING_MTR bundles measure in metres and frequently
  // carry one decimal (e.g. 74.2 M). Two bugs were biting on this flow:
  //   1. `parseInt(receivedQty)` stripped the decimal, turning 74.20
  //      into 74 — the operator was forced to undercount.
  //   2. Even after switching to `parseFloat`, raw `bundleQty - recvQty`
  //      produces floating-point noise like `0.20000000000000284`,
  //      triggering a phantom Shortage badge + the remarks gate.
  // Fix: parseFloat for RUNNING_MTR + round both side to 2 decimals
  // before comparing + apply a 0.005 m tolerance to absorb scale rounding.
  const FLOAT_TOL = 0.005;
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  const recvQty = isRunningMtr
    ? round2(parseFloat(receivedQty) || 0)
    : (parseInt(receivedQty) || 0);
  const rawDelta = round2(bundleQty - recvQty);
  const shortage = rawDelta > FLOAT_TOL ? round2(rawDelta) : 0;
  const excess = -rawDelta > FLOAT_TOL ? round2(-rawDelta) : 0;
  const isMatch = shortage === 0 && excess === 0;

  const handleConfirm = async () => {
    if (!bundleDetails || staffList.length === 0) return;
    if (recvQty <= 0) {
      toast.error('Received quantity must be greater than 0');
      return;
    }
    if ((shortage > 0 || excess > 0) && !remarks.trim()) {
      toast.error('Please provide remarks for shortage or excess');
      return;
    }

    setConfirming(true);
    try {
      const res = await apiClient.post('/processing-inward/confirm', {
        bundle_code: bundleDetails.bundle_code,
        received_qty: recvQty,
        remarks: remarks.trim(),
        staff_ids: staffList.map((s) => s.staff_id),
      });
      toast.success(res.data.message);
      setLastReceipt(res.data);

      // Auto-open print dialog for bundle sticker
      setPrintCodes([bundleDetails.bundle_code]);
      setShowPrintDialog(true);

      setBundleDetails(null);
      setBundleCode('');
      setReceivedQty('');
      setRemarks('');
    } catch (err) {
      const det = err.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : det?.message || 'Failed to confirm receipt');
    } finally {
      setConfirming(false);
    }
  };

  const handlePrintClose = () => {
    setShowPrintDialog(false);
    setPrintCodes([]);
    setTimeout(() => scanRef.current?.focus(), 100);
  };

  // === STAFF SESSION SCREEN (multi-staff) ===
  if (step === 'staff') {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="processing-inward-page">
        <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Processing Inward</h1>
            <p className="text-xs text-gray-500">Receive goods from Processing</p>
          </div>
        </div>
        <div className="max-w-md mx-auto p-6 mt-8">
          <Card className="border-blue-200">
            <CardHeader className="text-center pb-2">
              <Users className="w-12 h-12 text-blue-600 mx-auto mb-2" />
              <CardTitle className="text-lg">Add Staff Working on This Session</CardTitle>
              <p className="text-sm text-gray-500">Scan one or more Staff IDs. Received qty is split equally across them.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <form onSubmit={handleAddStaff} className="flex gap-2">
                <Input
                  ref={staffInputRef}
                  value={staffValue}
                  onChange={(e) => setStaffValue(e.target.value)}
                  placeholder="Staff ID (e.g., V0104)"
                  autoFocus
                  data-testid="staff-scan-input"
                />
                <Button type="submit" disabled={validating || !staffValue.trim()} data-testid="staff-add-btn">
                  {validating ? '...' : (<><Plus className="w-4 h-4 mr-1" />Add</>)}
                </Button>
              </form>

              {staffList.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-gray-500 font-medium">{staffList.length} staff added:</p>
                  {staffList.map((s) => (
                    <div
                      key={s.staff_id}
                      className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                      data-testid={`staff-chip-${s.staff_id}`}
                    >
                      <UserCheck className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-800 text-sm flex-1">{s.staff_name}</span>
                      <span className="text-xs text-gray-500">{s.staff_id}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        onClick={() => handleRemoveStaff(s.staff_id)}
                        data-testid={`staff-remove-${s.staff_id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="w-full"
                size="lg"
                disabled={staffList.length === 0}
                onClick={handleStartSession}
                data-testid="start-session-btn"
              >
                Start Session ({staffList.length} staff)
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // === MAIN OPERATION SCREEN ===
  return (
    <div className="min-h-screen bg-gray-50" data-testid="processing-inward-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-gray-900">Processing Inward</h1>
          <p className="text-xs text-gray-500">Receive goods from Processing — verify quantity</p>
        </div>
        <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg" data-testid="staff-badge">
          <Users className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">{staffList.length} staff</span>
          <span className="text-xs text-blue-500 hidden sm:inline">({staffList.map((s) => s.staff_id).join(', ')})</span>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-blue-600 h-6 px-1.5"
            onClick={() => setStep('staff')}
            data-testid="change-staff-btn"
          >
            Change
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Bundle Scan */}
        <Card>
          <CardContent className="pt-4">
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Scan / Enter Bundle Number</label>
            <div className="flex gap-2">
              <Input
                ref={scanRef}
                value={bundleCode}
                onChange={(e) => setBundleCode(e.target.value)}
                onKeyDown={handleScanKeyDown}
                placeholder="e.g., BUN042253/25-26"
                autoFocus
                data-testid="bundle-scan-input"
              />
              <Button onClick={handleLookup} disabled={loading || !bundleCode.trim()} data-testid="lookup-btn">
                {loading ? 'Loading...' : <Search className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bundle Details */}
        {bundleDetails && (
          <Card className="border-blue-200 bg-blue-50/30" data-testid="bundle-details-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                Bundle Details — {bundleDetails.bundle_code}
                {bundleDetails.flow_type && bundleDetails.flow_type !== 'REGULAR' && (
                  <span
                    className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded"
                    data-testid={`flow-type-badge-${bundleDetails.flow_type}`}
                  >
                    {bundleDetails.flow_type === 'RUNNING_MTR'
                      ? `Running Mtr · ${bundleDetails.received_mtr ?? '?'} m`
                      : bundleDetails.flow_type === 'SHIRTING'
                      ? `Shirting · ${bundleDetails.consumption_per_pc ?? '?'} m/pc`
                      : bundleDetails.flow_type}
                  </span>
                )}
              </CardTitle>
              {bundleDetails.matched_via === 'original_bundle' && bundleDetails.original_bundle && (
                <p
                  className="text-xs text-blue-700 mt-0.5"
                  data-testid="matched-via-original-hint"
                >
                  Matched via corrected original bundle <span className="font-mono">{bundleDetails.original_bundle}</span>. Receiving against gate code <span className="font-mono">{bundleDetails.bundle_code}</span>.
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {bundleDetails.sort_no && (
                  <div>
                    <span className="text-gray-500">Sort No</span>
                    <p className="font-semibold" data-testid="detail-sort-no">{bundleDetails.sort_no}</p>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Color</span>
                  <p className="font-semibold" data-testid="detail-color">{bundleDetails.color || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Design</span>
                  <p className="font-semibold" data-testid="detail-design">{bundleDetails.design_name || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Barcode / SKU</span>
                  <p className="font-mono font-semibold" data-testid="detail-barcode">{bundleDetails.barcode || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Sent Qty (USNs)</span>
                  <p className="font-bold text-lg text-blue-700" data-testid="detail-sent-qty">{bundleDetails.sent_qty}</p>
                </div>
                {/* 2026-05-14 — Show the actual bundle qty (meters for
                    RUNNING_MTR, pieces otherwise) separately from the USN
                    count, so operators see what they're verifying against. */}
                {isRunningMtr && (
                  <div>
                    <span className="text-gray-500">Bundle Qty</span>
                    <p className="font-bold text-lg text-emerald-700" data-testid="detail-bundle-qty">
                      {bundleQty} {qtyUnit}
                    </p>
                  </div>
                )}
                {bundleDetails.vendor_name && (
                  <div>
                    <span className="text-gray-500">Vendor</span>
                    <p className="font-semibold">{bundleDetails.vendor_name}</p>
                  </div>
                )}
                {bundleDetails.bin_location && (
                  <div>
                    <span className="text-gray-500">Bin Location</span>
                    <p className="font-semibold">{bundleDetails.bin_location}</p>
                  </div>
                )}
              </div>

              {/* Received Qty */}
              <div className="pt-3 border-t">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Received Quantity {isRunningMtr ? `(${qtyUnit})` : ''}
                </label>
                <Input
                  type="text"
                  inputMode={isRunningMtr ? 'decimal' : 'numeric'}
                  pattern={isRunningMtr ? '[0-9]*[.]?[0-9]*' : '[0-9]*'}
                  value={receivedQty}
                  onChange={(e) => {
                    let v = e.target.value;
                    if (isRunningMtr) {
                      v = v.replace(/[^0-9.]/g, '');
                      const parts = v.split('.');
                      if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
                    } else {
                      v = v.replace(/[^0-9]/g, '');
                    }
                    setReceivedQty(v);
                  }}
                  className="text-lg font-bold w-32"
                  data-testid="received-qty-input"
                />
                <div className="mt-2 flex items-center gap-2">
                  {recvQty > 0 && isMatch && (
                    <Badge className="bg-green-100 text-green-800 border-green-200" data-testid="status-match">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Quantity Match
                    </Badge>
                  )}
                  {shortage > 0 && (
                    <Badge className="bg-red-100 text-red-800 border-red-200" data-testid="status-shortage">
                      <MinusCircle className="w-3.5 h-3.5 mr-1" /> Shortage: {shortage}
                    </Badge>
                  )}
                  {excess > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="status-excess">
                      <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Excess: {excess}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Remarks */}
              {(shortage > 0 || excess > 0) && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Remarks <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Explain shortage or excess..."
                    rows={2}
                    data-testid="remarks-input"
                  />
                </div>
              )}
              {isMatch && recvQty > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Remarks (optional)</label>
                  <Textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional notes..."
                    rows={2}
                    data-testid="remarks-input"
                  />
                </div>
              )}

              <Button
                className="w-full mt-2"
                size="lg"
                onClick={handleConfirm}
                disabled={confirming || recvQty <= 0}
                data-testid="confirm-btn"
              >
                {confirming ? 'Confirming...' : (
                  isRunningMtr
                    ? `Confirm Receipt — ${recvQty.toFixed(2)} ${qtyUnit}`
                    : `Confirm Receipt — ${recvQty} USNs`
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Last Receipt Summary */}
        {lastReceipt && (
          <Card className="border-green-200 bg-green-50/30" data-testid="receipt-summary-card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-bold text-green-800">Receipt Confirmed</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Receipt ID:</span> <span className="font-mono">{lastReceipt.receipt_id}</span></div>
                <div><span className="text-gray-500">Bundle:</span> <span className="font-semibold">{lastReceipt.bundle_code}</span></div>
                <div><span className="text-gray-500">Sent:</span> {lastReceipt.sent_qty}</div>
                <div><span className="text-gray-500">Received:</span> {lastReceipt.received_qty}</div>
                {lastReceipt.shortage_qty > 0 && <div className="text-red-600">Shortage: {lastReceipt.shortage_qty}</div>}
                {lastReceipt.excess_qty > 0 && <div className="text-amber-600">Excess: {lastReceipt.excess_qty}</div>}
                <div><span className="text-gray-500">Status:</span>
                  <Badge className={
                    lastReceipt.receipt_status === 'confirmed' ? 'bg-green-100 text-green-800 ml-1' :
                    lastReceipt.receipt_status === 'shortage' ? 'bg-red-100 text-red-800 ml-1' :
                    'bg-amber-100 text-amber-800 ml-1'
                  }>
                    {lastReceipt.receipt_status}
                  </Badge>
                </div>
                <div><span className="text-gray-500">Staff:</span> {staffList.map((s) => s.staff_name).join(', ')}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => { setPrintCodes([lastReceipt.bundle_code]); setShowPrintDialog(true); }}
                data-testid="reprint-sticker-btn"
              >
                <Printer className="w-4 h-4 mr-1" /> Reprint Bundle Sticker
              </Button>
            </CardContent>
          </Card>
        )}

        {/* View History Link */}
        <div className="text-center pt-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/sections/inhouse/processing-inward/history')} data-testid="view-history-btn">
            <FileText className="w-4 h-4 mr-1" /> View Receipts History
          </Button>
        </div>
      </div>

      {/* Bundle Sticker Print Dialog */}
      <PrintDialog
        open={showPrintDialog}
        onOpenChange={handlePrintClose}
        codes={printCodes}
        labelType="bundle"
        operationKey="processing-inward"
        title="Print Bundle Sticker"
      />
    </div>
  );
};

export default ProcessingInward;
